import { common as c } from '@pendo/aggregations';
import { FunnelFilter, FunnelGroupConfig, FunnelItem } from '@/types/analytics/funnels.types';
import { FilterType, MetadataTypeId, MetadataValue, PageParameterId } from '@/types/analytics/analytics.types';
import { createFilterString } from '@middleware/common';
import cloneDeep from 'lodash/cloneDeep';

export default function generateFunnelConfigPipeline (
    config: c.PipelineFunnelData,
    groupings: Array<FunnelGroupConfig> | undefined,
) {
    let hasDevicePropFilter = false;
    let hasRegionFilter = false;
    const bulkExpandTotal: string[] = [];
    const clonedConfig = cloneDeep(config);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const funnelAggregatorConfig = Object.entries(clonedConfig).reduce((acc: any, [param, value]: any) => {
        if (!['uniqueVisitorFunnel', 'maxDuration', 'items', 'eventproperty'].includes(param)) return acc;
        if (param === 'items') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value.forEach(({ filters, guideEventTypes }: FunnelItem, idx: number, items: Record<string, any>) => {
                if (filters && filters.length) {
                    let filterString = '';
                    filters.forEach((filter: FunnelFilter, filterIdx: number) => {
                        filterString += `(${createFilterString(filter, getFilterIdentifier)})`;

                        if (filterIdx !== filters.length - 1) {
                            filterString += '&&';
                        }

                        if (filter.filterType === FilterType.Device) {
                            hasDevicePropFilter = true;
                        }

                        if (filter.filterType === FilterType.Location && filter.field === 'region') {
                            hasRegionFilter = true;
                        }

                        if (filter.filterType === FilterType.Visitor && filter.field !== 'id') {
                            bulkExpandTotal.push(filter.filterType);
                        }

                        if (filter.filterType === FilterType.Account && filter.field !== 'id') {
                            bulkExpandTotal.push(filter.filterType);
                        }

                        if (filter.filterType === FilterType.ParentAccount) {
                            bulkExpandTotal.push(filter.filterType);
                        }
                    });

                    items[idx].filter = filterString;
                }
                const guideEventFilters: { [key: string]: string } = {
                    guideNotSeen: '(guideSeenReason=="control")',
                };
                if (guideEventTypes && Object.keys(guideEventFilters).includes(guideEventTypes)) {
                    if (filters && filters.length) {
                        items[idx].filter += `&&${guideEventFilters[guideEventTypes]}`;
                    } else {
                        items[idx].filter = guideEventFilters[guideEventTypes];
                    }
                }

                delete items[idx].filters;
            });
        }

        acc[`${param}`] = value;

        return acc;
    }, {});

    funnelAggregatorConfig.eventproperty =
        groupings && groupings[0]?.configuration ? getGroupIdentifier(groupings[0]) : undefined;

    // this parameter drops all events that don't fall into the funnel
    funnelAggregatorConfig.onlyMatchedEvents = true;

    // dedupe bulkExpand
    const bulkExpand = [...new Set([...bulkExpandTotal])];

    return {
        funnelAggregatorConfig,
        hasDevicePropFilter,
        bulkExpand,
        hasRegionFilter,
    };
}

export function getFilterIdentifier (configuration: FunnelFilter) {
    if (configuration.filterType === FilterType.Location && configuration.field === 'region') return 'country_region';
    if (configuration.filterType === FilterType.PageParameter) return 'parameters.parameter';
    if (configuration.filterType === FilterType.EventTopLevel || configuration.filterType === FilterType.Location) {
        return `${configuration.field}`;
    }

    return getIdentifier(configuration, configuration.filterType);
}

function getGroupIdentifier ({ configuration, type }: FunnelGroupConfig) {
    return getIdentifier(configuration, type);
}

function getIdentifier (configuration: FunnelFilter | MetadataValue, type: string) {
    if (type === FilterType.Visitor && configuration.field === 'id') {
        return 'visitorId';
    }
    if (type === FilterType.Account && configuration.field === 'id') {
        return 'accountId';
    }

    if (type === MetadataTypeId.Event && configuration.field === 'region') {
        return 'country_region';
    }
    if (type === MetadataTypeId.Event && configuration.field === 'country') {
        return 'country';
    }

    if (type === MetadataTypeId.Event && configuration.field === 'parameter') {
        return 'parameters.parameter';
    }

    if (type === MetadataTypeId.Event && configuration.field === 'server') {
        return 'server';
    }

    switch (type) {
        case MetadataTypeId.Utm:
        case FilterType.Utm:
            return `properties.__utm__.${configuration.field}`;
        case MetadataTypeId.Historical:
        case FilterType.HistoricalMetadata:
            return `properties.__sg__.${configuration.kind}metadata.${configuration.group}__${configuration.field}`;
        case FilterType.Device:
            return `parsedUserAgent.${configuration.field}`;
        case FilterType.ParentAccount:
        case FilterType.Account:
        case FilterType.Visitor:
            return `${configuration.kind}.${configuration.group}.${configuration.field}`;
        case MetadataTypeId.Event:
        case FilterType.Custom:
            // When grouping by device props, the type is event from the first multiselect
            if (configuration.kind === MetadataTypeId.Automatic) {
                return `parsedUserAgent.${configuration.field}`;
            }

            return `properties.${configuration.field}`;
        case MetadataTypeId.PageParameter:
        case FilterType.PageParameter:
            return PageParameterId;
        case MetadataTypeId.EventTopLevel:
        case FilterType.Location:
            return configuration.field;
        case FilterType.EventTopLevel:
            return `${configuration.field}`;
    }

    return undefined;
}
